/**
 * LocaleFormatExample Component (ORCH-413)
 *
 * Purpose: Shows, under the profile's Locale and Time Format, what that formatting
 *          looks like: long date, short date with its pattern, time, number and
 *          currency amount. Follows the values in the form, before saving.
 * Logic: Reads Locale and Time Format from the surrounding TemplateForm and formats a
 *        fixed sample with an explicit locale and clock — never the active one, since
 *        on the User Manager page the form is someone else's. The currency is the
 *        data's (the tenant's base currency); the locale only formats it (ORCH-191).
 * Dependencies: useTemplateFormContext, utils/datetime, utils/formatting, utils/profileFormatting
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTemplateFormContext } from '../template-form';
import type { MasterDataValue } from '../../types/mdm';
import { formatDate, formatTime, shortDatePattern } from '../../utils/datetime';
import { formatCurrency, formatNumber } from '../../utils/formatting';
import { DEFAULT_LOCALE } from '../../utils/locale';
import {
  LOCALE_FIELD,
  TIME_FORMAT_FIELD,
  hourCycleCodeOf,
  localeTagOf,
} from '../../utils/profileFormatting';

/** A fixed instant and amounts so the example reads the same for everyone. */
const SAMPLE_INSTANT = new Date(2026, 4, 22, 14, 30);
const SAMPLE_NUMBER = 1234567.89;
const SAMPLE_AMOUNT = 1234.56;

export interface LocaleFormatExampleProps {
  locales: MasterDataValue[];
  hourCycles: MasterDataValue[];
  /** ISO 4217 code the currency sample is in */
  currencyCode: string;
}

const LocaleFormatExample: React.FC<LocaleFormatExampleProps> = ({
  locales,
  hourCycles,
  currencyCode,
}) => {
  const { values } = useTemplateFormContext();
  const locale = localeTagOf(values[LOCALE_FIELD], locales) ?? DEFAULT_LOCALE;
  const hourCycle = hourCycleCodeOf(values[TIME_FORMAT_FIELD], hourCycles);
  const shortDate = shortDatePattern(SAMPLE_INSTANT, locale);

  const lines: Array<[string, string]> = [
    ['Long date', formatDate(SAMPLE_INSTANT, 'full', locale)],
    ['Short date', `${shortDate.pattern}: ${shortDate.sample}`],
    ['Time', formatTime(SAMPLE_INSTANT, 'short', locale, hourCycle)],
    ['Number', formatNumber(SAMPLE_NUMBER, 2, locale)],
    ['Currency', formatCurrency(SAMPLE_AMOUNT, currencyCode, locale)],
  ];

  return (
    <Box data-testid="locale-format-example">
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
        Example
      </Typography>
      <Box
        component="dl"
        sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, m: 0 }}
      >
        {lines.map(([label, sample]) => (
          <React.Fragment key={label}>
            <Typography component="dt" variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography
              component="dd"
              variant="body2"
              sx={{ m: 0 }}
              data-testid={`locale-format-example-${label.toLowerCase().replace(' ', '-')}`}
            >
              {sample}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default LocaleFormatExample;
